import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { REGISTER_OPTIN_PARAM, REGISTER_SIGNUP_PARAM, ROUTE_HOME } from '../../../../config/routes';
import GlobalLogin, { viewType } from '../../../../global/GlobalLogin';
import { getDecryptedData } from '../../../../utils/crypto-utils';
import { finishProcess } from '../../../../utils/login';
import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { POSTHOG_PROPERTIES } from '../../../../config/tracker';
import { TABS } from '../../../../utils/tbbRegistration';

import './Login.scss';

const Login = ({
  storeConfig,
  onSubmit,
  onError,
  emailLinkParams,
  onEmailLinkSentAuth,
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const { isSignedIn, isLoading: isLoadingFirebaseAuth, userData } = useFirebaseAuth();
  const posthog = usePostHog();

  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [finishProcessData, setFinishProcessData] = useState({});
  const [view, setView] = useState(viewType.VIEW_EMAIL_FORM);
  const hasRedirectedRef = useRef(false);

  const redirect = useCallback(async () => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    setIsLoading(true);

    const {
      error: userError,
      uid: userUid,
    } = userData;

    if (userError) {
      hasRedirectedRef.current = false;
      onError(userError);
      setIsLoading(false);
      return;
    }

    if (emailLinkSent) {
      setIsLoading(false);
      if (onEmailLinkSentAuth) {
        onEmailLinkSentAuth();
      } else {
        history.push(ROUTE_HOME);
      }
      return;
    }

    try {
      const query = new URLSearchParams(search);
      const data = await getDecryptedData(query, userUid);
      await finishProcess({
        ...finishProcessData,
        ...(REGISTER_SIGNUP_PARAM in data && { userRegistered: data[REGISTER_SIGNUP_PARAM] }),
        ...(REGISTER_OPTIN_PARAM in data && { optIn: data[REGISTER_OPTIN_PARAM] }),
        userData,
        search,
        posthog,
        trackerProps: {
          [POSTHOG_PROPERTIES.TBB_STEP]: TABS.LOGIN,
          [POSTHOG_PROPERTIES.PARTNER_ID]: storeConfig.id,
          [POSTHOG_PROPERTIES.PARTNER_NAME]: storeConfig.title,
        },
      });

      onSubmit(data);
    } catch (error) {
      hasRedirectedRef.current = false;
      setIsLoading(false);
      onError(error);
    }
  }, [userData, onSubmit, onError, emailLinkSent, onEmailLinkSentAuth, search, finishProcessData]);

  const handleOnEmailLinkSent = useCallback(() => {
    setEmailLinkSent(true);
  }, []);

  useEffect(() => {
    if (isSignedIn && userData && !isLoadingFirebaseAuth) {
      redirect();
    }
  }, [isSignedIn, userData, isLoadingFirebaseAuth]);

  return (
    <div className="RegisterLogin">
      <GlobalLogin
        isParentLoading={isLoading || isLoadingFirebaseAuth}
        storeConfig={storeConfig}
        emailLinkParams={emailLinkParams}
        onEmailLinkSent={handleOnEmailLinkSent}
        setFinishProcessData={setFinishProcessData}
        view={view}
        setView={setView}
        isEmbedded
      />
    </div>
  );
};

export default Login;
